import {
  getFullDeepLinkURL,
  type DeepLink,
} from "@/components/common/deep-link/deep-link-encoding";
import type { ViewRuntimeContextData } from "@/components/common/view-runtime-context";
import { decodeCameraParameters } from "@/components/r3f/utils/camera-parameters";
import { decodeOrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { getMode } from "@/modes";
import type { ModeNames } from "@/modes/mode";
import { selectActiveCadId } from "@/store/cad/cad-slice";
import { Features } from "@/store/features/features";
import { selectHasFeature } from "@/store/features/features-slice";
import { selectModeName } from "@/store/mode-selectors";
import type { ModeInitialState } from "@/store/mode-slice";
import {
  selectCompareShouldUseIntensityData,
  selectShouldUseIntensityData,
} from "@/store/modes/walk-mode-slice";
import {
  selectHasActiveSearchFilters,
  selectProjectFilteringSlice,
} from "@/store/project-filtering/project-filtering-selectors";
import { selectActiveElementId } from "@/store/selections-selectors";
import type { RootState } from "@/store/store";
import { assert, type GUID } from "@faro-lotv/foundation";
import { createSavedView } from "@/features/saved-views/saved-views-client";
import type { SavedViewData } from "@faro-lotv/sphere-viewer-contracts";

/** The 3d view context a capture reads the cameras and controls from. */
type ViewContext = Omit<ViewRuntimeContextData, "setCameraData">;

/**
 * Captures the active mode's state in its structured, unencoded form.
 *
 * A mode's `compute` produces the compact string encoding used by legacy deep
 * link URLs, so the three geometry fields are decoded back into objects here.
 * Every other field a mode contributes (`scene`, `rightId`, `rightScene`) is
 * already a plain string in both representations and passes straight through.
 *
 * This deliberately does not route back through the mode's `parse`: that function
 * validates untrusted URL input against the project and may reject or throw,
 * which is the wrong behaviour when capturing state the app is currently
 * displaying. It is the exact inverse of `toModeInitialState` in
 * `apply-saved-view.ts`.
 *
 * @param mode The active mode.
 * @param state The current app state.
 * @param view The current 3d view context.
 * @returns the structured mode state, empty if the mode has none.
 */
export function captureModeState(
  mode: ModeNames,
  state: RootState,
  view: ViewContext,
): Partial<SavedViewData> {
  const { initialState } = getMode(mode);
  if (!initialState) return {};

  const encoded: ModeInitialState = initialState.compute(state, view);
  return {
    ...encoded,
    camera: decodeCameraParameters(encoded.camera),
    clipping: decodeOrientedBoundingBox(encoded.clipping),
    rightCam: decodeCameraParameters(encoded.rightCam),
  };
}

/** Options describing which state a saved view should capture. */
export type CaptureSavedViewOptions = {
  /** An optional element to look at, distinct from the active element. */
  lookAtId?: GUID;

  /**
   * Whether the current view context (active element, mode, cad, mode-specific
   * state) should be captured. When false, only the `lookAtId` is stored, letting
   * the viewer decide how to display the element.
   */
  includeContext: boolean;
};

/**
 * Captures the current app state into a structured {@link SavedViewData}.
 *
 * @param state The current app state.
 * @param view The current 3d view context.
 * @param options Controls whether the full context or only the lookAt element is captured.
 * @returns the structured saved view state to persist.
 */
export function captureSavedView(
  state: RootState,
  view: ViewContext,
  { lookAtId, includeContext }: CaptureSavedViewOptions,
): SavedViewData {
  if (!includeContext) {
    assert(
      lookAtId,
      "A lookAtId is required to capture a context-free saved view",
    );
    return { lookAtId };
  }

  const id = selectActiveElementId(state);
  assert(id, "Can't capture a saved view without an active element");
  const cadId = selectActiveCadId(state);
  const mode = selectModeName(state);
  const modeState = captureModeState(mode, state, view);

  return {
    ...modeState,
    id,
    mode,
    intensity: selectShouldUseIntensityData(state),
    // Only meaningful when a comparison view was captured.
    ...(modeState.rightId
      ? { rightIntensity: selectCompareShouldUseIntensityData(state) }
      : {}),
    ...(cadId ? { cadId } : {}),
    ...(lookAtId ? { lookAtId } : {}),
    ...(selectHasActiveSearchFilters(state)
      ? { filtering: selectProjectFilteringSlice(state) }
      : {}),
  };
}

/**
 * Captures the current app state into a legacy deep link URL.
 *
 * Unlike a saved view, a deep link encodes the whole view state directly in the
 * URL search params, so no backend persistence is involved. Used when the
 * {@link Features.ShortShare} feature is disabled.
 *
 * @param state The current app state.
 * @param view The current 3d view context.
 * @param options Controls whether the full context or only the lookAt element is captured.
 * @returns the legacy deep link URL pointing to the current view.
 */
export function captureDeepLink(
  state: RootState,
  view: ViewContext,
  { lookAtId, includeContext }: CaptureSavedViewOptions,
): URL {
  if (!includeContext) {
    assert(
      lookAtId,
      "A lookAtId is required to capture a context-free deep link",
    );
    return getFullDeepLinkURL({ lookAtId });
  }

  const id = selectActiveElementId(state);
  assert(id, "Can't create a deep link without an active element");
  const cadId = selectActiveCadId(state);
  const mode = selectModeName(state);
  const encodedModeState =
    getMode(mode).initialState?.compute(state, view) ?? {};

  const linkData: DeepLink = {
    ...encodedModeState,
    id,
    mode,
    ...(cadId ? { cadId } : {}),
    ...(lookAtId ? { lookAtId } : {}),
  };
  return getFullDeepLinkURL(linkData);
}

/**
 * Persists a saved view and builds the share link for it.
 *
 * No `ttl` is sent, so the view is stored permanently: legacy deep links never
 * expire, and a short link that dies would be a regression against them.
 *
 * @param projectId The id of the project the saved view belongs to.
 * @param data The structured saved view to persist.
 * @returns the `/share/<id>` URL pointing to the created saved view.
 * @throws if the saved view could not be created.
 */
export async function createShareLink(
  projectId: string,
  data: SavedViewData,
): Promise<URL> {
  const id = await createSavedView({ projectId, data });
  return new URL(`${window.location.origin}/share/${id}`);
}

/**
 * Creates a shareable link for the current view, choosing the link type based on
 * the {@link Features.ShortShare} feature.
 *
 * When the feature is enabled the view is persisted as a saved view and a short
 * `/share/<id>` link is returned; otherwise a legacy deep link encoding the whole
 * state in the URL search params is returned. This is the single decision point
 * between the two, so callers never branch on the feature themselves.
 *
 * @param projectId The id of the project the view belongs to.
 * @param state The current app state.
 * @param view The current 3d view context.
 * @param options Controls whether the full context or only the lookAt element is captured.
 * @returns the URL to share.
 * @throws if a saved view was required but could not be created.
 */
export function createViewShareLink(
  projectId: string,
  state: RootState,
  view: ViewContext,
  options: CaptureSavedViewOptions,
): Promise<URL> {
  if (selectHasFeature(Features.ShortShare)(state)) {
    return createShareLink(projectId, captureSavedView(state, view, options));
  }
  return Promise.resolve(captureDeepLink(state, view, options));
}
